import { useEffect, useState } from "react";
import { fetchAdminShopSettings, updateAdminShopSettings } from "../api";

const SHIPPING_METHODS = [
  { id: "pickup", label: "Személyes átvétel" },
  { id: "foxpostLocker", label: "Foxpost csomagpont" },
  { id: "glsLocker", label: "GLS csomagpont" },
  { id: "foxpostHome", label: "Foxpost házhozszállítás" },
  { id: "glsHome", label: "GLS házhozszállítás" },
];

function formatDate(value) {
  if (!value) return null;
  try {
    return new Intl.DateTimeFormat("hu-HU", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch {
    return null;
  }
}

function AdminShopSettings({ token, adminUsername }) {
  const [coffeePriceHuf, setCoffeePriceHuf] = useState(0);
  const [coffeeEnabled, setCoffeeEnabled] = useState(false);
  const [webshopEnabled, setWebshopEnabled] = useState(false);
  const [shipping, setShipping] = useState({});
  const [shopUpdatedAt, setShopUpdatedAt] = useState(null);
  const [shopUpdatedBy, setShopUpdatedBy] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) return;

    let cancelled = false;

    async function load() {
      setLoading(true);
      setError("");

      try {
        const data = await fetchAdminShopSettings(token);
        if (cancelled) return;
        setCoffeePriceHuf(data.coffeePriceHuf || 0);
        setCoffeeEnabled(data.coffeeEnabled === true);
        setWebshopEnabled(data.webshopEnabled === true);
        setShipping(data.shipping || {});
        setShopUpdatedAt(data.shopUpdatedAt || null);
        setShopUpdatedBy(data.shopUpdatedBy || "");
      } catch {
        if (!cancelled) setError("Nem sikerült betölteni a webshop beállításokat.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  function updateShippingField(id, field, value) {
    setShipping((current) => ({
      ...current,
      [id]: {
        ...current[id],
        [field]: value,
      },
    }));
  }

  async function handleSave(event) {
    event.preventDefault();
    if (saving) return;

    setSaving(true);
    setMessage("");
    setError("");

    try {
      const data = await updateAdminShopSettings(token, {
        coffeePriceHuf: Number(coffeePriceHuf) || 0,
        coffeeEnabled,
        webshopEnabled,
        shipping,
        updatedBy: adminUsername || "",
      });
      setCoffeePriceHuf(data.coffeePriceHuf || 0);
      setCoffeeEnabled(data.coffeeEnabled === true);
      setWebshopEnabled(data.webshopEnabled === true);
      setShipping(data.shipping || {});
      setShopUpdatedAt(data.shopUpdatedAt || null);
      setShopUpdatedBy(data.shopUpdatedBy || "");
      setMessage("Webshop beállítások mentve.");
    } catch {
      setError("A mentés sikertelen.");
    } finally {
      setSaving(false);
    }
  }

  const formattedUpdatedAt = formatDate(shopUpdatedAt);

  return (
    <>
      <header className="admin-dash-header">
        <div>
          <h1 className="admin-dash-title">Webshop</h1>
          <p className="admin-dash-subtitle">
            Mizu Coffee ára és szállítási díjak beállítása.
          </p>
        </div>
      </header>

      <section className="admin-settings-card">
        {loading ? (
          <p className="admin-dash-empty">Betöltés…</p>
        ) : (
          <form className="admin-shop-form" onSubmit={handleSave}>
            <div className="admin-shop-row">
              <label className="admin-shop-field">
                <span>Mizu Coffee ára (1 kg, Ft)</span>
                <input
                  type="number"
                  min="175"
                  step="1"
                  value={coffeePriceHuf}
                  onChange={(event) => setCoffeePriceHuf(event.target.value)}
                />
              </label>
              <p className="admin-settings-note">
                Stripe minimum: 175 Ft. Az ár forintban értendő.
              </p>

              <label className="admin-shop-toggle">
                <input
                  type="checkbox"
                  checked={coffeeEnabled}
                  onChange={(event) => setCoffeeEnabled(event.target.checked)}
                />
                <span>Online vásárlás engedélyezve</span>
              </label>
            </div>

            <div className="admin-shop-row">
              <label className="admin-shop-toggle">
                <input
                  type="checkbox"
                  checked={webshopEnabled}
                  onChange={(event) => setWebshopEnabled(event.target.checked)}
                />
                <span>Webshop megnyitva</span>
              </label>
              <p className="admin-settings-note">
                Kikapcsolva a /webshop oldalon „Hamarosan” jelenik meg, és kosárral nem lehet
                fizetni. A Mizu Coffee fenti vásárlása ettől függetlenül működik. A termékeket a
                Termékek fülön zárt webshop mellett is előkészítheted.
              </p>
            </div>

            <div className="admin-shop-shipping">
              <h2 className="admin-shop-shipping-title">Szállítási módok</h2>
              {SHIPPING_METHODS.map((method) => {
                const entry = shipping[method.id] || { enabled: true, priceHuf: 0 };
                return (
                  <div key={method.id} className="admin-shop-shipping-row">
                    <label className="admin-shop-toggle">
                      <input
                        type="checkbox"
                        checked={entry.enabled !== false}
                        onChange={(event) =>
                          updateShippingField(method.id, "enabled", event.target.checked)
                        }
                      />
                      <span>{method.label}</span>
                    </label>
                    <label className="admin-shop-field admin-shop-field--compact">
                      <span>Díj (Ft)</span>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={entry.priceHuf ?? 0}
                        onChange={(event) =>
                          updateShippingField(
                            method.id,
                            "priceHuf",
                            Number(event.target.value) || 0,
                          )
                        }
                      />
                    </label>
                  </div>
                );
              })}
            </div>

            {formattedUpdatedAt ? (
              <p className="admin-settings-meta">
                Utoljára módosítva: {formattedUpdatedAt}
                {shopUpdatedBy ? ` — ${shopUpdatedBy}` : ""}
              </p>
            ) : null}

            {message ? <p className="admin-settings-message">{message}</p> : null}
            {error ? <p className="admin-settings-error">{error}</p> : null}

            <button type="submit" className="newsletter-modal-submit" disabled={saving}>
              {saving ? "Mentés…" : "Mentés"}
            </button>
          </form>
        )}
      </section>
    </>
  );
}

export default AdminShopSettings;
